export type User = {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  emailVerified: boolean;
  fullAccess: boolean;
  deleted: boolean;
  membershipType: MembershipType;
  redeemAccessCode?: string;
  role: string;
};

export type Coach = {
  id: number;
  firstName: string;
  email: string;
  createdAt: string;
  emailVerificationCode: string;
  deleted: boolean;
};

export type CoachMembershipDto = {
  id: string;
  organizationId: string;
  organizationName: string;
  role: string;
  accessCode: string;
};

export type OrganizationT = {
  id: number;
  name: string;
  accessUntil: Date;
};

export type MembershipInfo = {
  paymentProcessor: string;
  type: MembershipType;
  accessUntil: Date;
  canceled: boolean;
  trial: boolean;
  email: string;
};

export type UpdateUserRequest = {
  id: number;
  membershipType: MembershipType;
};

export type InviteUserRequest = {
  name: string;
  email: string;
};

export type CreateOrUpdateOrganizationRequest = {
  name: string;
  accessUntil: Date;
  sendyListId: string | null;
};

export type OrganizationDto = {
  id: number;
  name: string;
  accessUntil: Date;
  sendyListId: string | null;
};

export type ScheduledEmailDto = {
  id: number;
  type: ScheduledEmailType;
  scheduledAt: Date;
  email: string;
  firstName: string;
};

export type Sort = {
  direction: string;
  property: string;
  ignoreCase: boolean;
  nullHandling: string;
  ascending: boolean;
  descending: boolean;
};

export type Pageable = {
  last: boolean;
  totalPages: number;
  totalElements: number;
  size: number;
  number: number;
  first: boolean;
  sort: Array<Sort>;
  numberOfElements: number;
};

export type AutoPlanFeedback = {
  id: number;
  userId: number;
  email: string;
  firstName: string;
  text: string;
  rangeFrom: string;
  rangeTo: string;
  createdAt: string;
};

export type AutoPlanFeedbackPageable = {
  content: Array<AutoPlanFeedback>;
} & Pageable;

export type UsersPageable = {
  content: Array<User>;
} & Pageable;

export type CoachesPageable = {
  content: Array<User>;
} & Pageable;

export type OrganizationsPageable = {
  content: Array<OrganizationT>;
} & Pageable;

export type MembershipPageable = {
  content: Array<MembershipInfo>;
} & Pageable;

export type ScheduledEmailsPageable = {
  content: Array<MembershipInfo>;
} & Pageable;

export type ImportUsersInOrganizationResponse = {
  message: string;
  user: User[];
};

export enum MembershipType {
  MONTHLY = 'MONTHLY',
  SEMI_ANNUAL = 'SEMI_ANNUAL',
  ANNUAL = 'ANNUAL',
  LIFETIME = 'LIFETIME',
  OLD_USER = 'OLD_USER',
  INVITED = 'INVITED',
  ORGANIZATION = 'ORGANIZATION',
  FREE_TRIAL = 'FREE_TRIAL',
  CANCELED = 'CANCELED'
}

export enum MembershipStatus {
  ACTIVE = 'ACTIVE',
  TRIAL = 'TRIAL',
  CANCELED = 'CANCELED',
  EXPIRED = 'EXPIRED'
}

export enum PaymentProcessor {
  STRIPE = 'STRIPE',
  BRAINTREE = 'BRAINTREE'
}

export enum ScheduledEmailType {
  RESET_TRIAL = 'RESET_TRIAL',
  RESET_TRIAL_REMINDER = 'RESET_TRIAL_REMINDER',
  TRIAL_EXPIRATION = 'TRIAL_EXPIRATION',
  GIFT_MEMBERSHIP_REMINDER = 'GIFT_MEMBERSHIP_REMINDER',
  MOBILE_SIGNUP_REMINDER = 'MOBILE_SIGNUP_REMINDER'
}

export type CancelSubscriptionResponse = {
  message: string;
};

export type AdminAnalyticsResponse = {
  from: Date;
  usersSignedUp: number;
  usersSubscribed: number;
  usersExpiredTrial: number;
  giftPurchases: number;
  parentPurchases: number;
  studentUpfrontPurchases: number;
  signUpsPerDay: {
    [key: string]: number;
  };
  usersSubscribedPerDay: {
    [key: string]: number;
  };
  usersExpiredTrialPerDay: {
    [key: string]: number;
  };
};

export type ResetTrialAnalyticsResponse = {
  resetTrialCampaigns: {
    [key: string]: {
      usersGiven: number;
      usersRestarted: number;
      usersSubscribed: number;
    };
  };
};

export enum AdminAnalyticsTimePeriod {
  LAST_WEEK = 'LAST_WEEK',
  LAST_MONTH = 'LAST_MONTH',
  LAST_3_MONTH = 'LAST_3_MONTH',
  // UNCOMMENT WHEN ANALYTICS FUNCTION IS OPTIMIZED
  // LAST_YEAR = 'LAST_YEAR',
  ALL = 'ALL'
}
